import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import MainNpo from './main'
import RolesNpo from './roles'
import { Link, useParams } from 'react-router-dom'
import MarkDownPreview from '@uiw/react-markdown-preview'
import MembersNpo from './members'
import TokensNpo from './tokens'
import { contractRead, toast } from '@/shared/lib'
import { ConfigStore, LangStore } from '@/shared/model/store'
import { Copy, ExternalLink } from 'lucide-react'
import { useEffect, useState } from 'react'
import useRequest from '@/shared/lib/use-request'
import { IUser } from '@/entities/user/model/types'
import { UserName } from '@/shared/ui/user-name'
import { Button } from '@/shared/ui'
import { useAccount } from 'wagmi'
import { IPFSProfileType } from '@/pages/profile/ui/profile'
import { getContentFromHash } from '@/widgets/post-editor/lib/utils'
import useIpfs from '@/entities/ipfs/use-ipfs'

const NPOPage = () => {
	const [admins, setAdmins] = useState<`0x${string}`[]>([])
	const { gateway, loaded } = useIpfs()
	const { domain } = ConfigStore()
	const [currentTab, setCurrentTab] = useState('main')
	const { fetch, result } = useRequest<IUser>('getUser')
	const [npoData, setNpoData] = useState<IUser & IPFSProfileType>({} as IUser & IPFSProfileType)
	const { address: wagmiAdress } = useAccount()
	const { id } = useParams()
	const { langPack } = LangStore()
	const npo_address: `0x${string}` | undefined = id as `0x${string}`

	const readIsAdmin = async () => {
		const res: `0x${string}`[] = await contractRead('SavvaNPO', 'getAdmins', [], npo_address)
		setAdmins(res)
	}

	const isAdmin = wagmiAdress ? admins.includes(wagmiAdress) : false

	const copy = () => {
		if (!id) return
		navigator.clipboard.writeText(id)
		toast({
			duration: 5000,
			key: 'copy_promo',
			title: langPack('profile.copied_text')
		})
	}

	const getNpoData = async () => {
		fetch({
			params: { user_addr: npo_address, domain },
			onSuccess: async res => {
				const userProfile = await getContentFromHash(res.profile_cid, gateway)
				const parced = userProfile ? JSON.parse(userProfile) : {}
				setNpoData({ ...res, ...parced })
			}
		})
	}

	useEffect(() => {
		if (!npo_address || !loaded) return
		getNpoData()

		readIsAdmin()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [npo_address, loaded])

	if (!npo_address || !result) return <></>

	return (
		<div className=''>
			<div className='mb-6 flex flex-col items-start justify-between px-4 py-6 shadow-lg'>
				<div className='flex items-center gap-2'>
					<UserName
						account={result}
						sliceLen={100}
						className='text-[16px]'
						extra={
							<div className='ml-4 flex gap-3'>
								<Button variant={'outline'} onClick={copy}>
									<Copy className='text-[grey]' size={16} />
								</Button>
								<Link to={`/${id}`}>
									<Button variant={'outline'}>
										<ExternalLink className='text-[grey]' size={16} />
									</Button>
								</Link>
							</div>
						}
					/>
				</div>
				<div>
					<MarkDownPreview
						source={npoData.about}
						className='bg-background'
						style={{ whiteSpace: 'pre-wrap', background: 'transparent', color: 'grey', fontSize: 12 }}
					/>
				</div>
			</div>

			{isAdmin ? (
				<Tabs value={currentTab} onValueChange={setCurrentTab}>
					<TabsList defaultValue={'main'}>
						<TabsTrigger value='main'>{langPack('npo.main')}</TabsTrigger>
						<TabsTrigger value='members' disabled={!npo_address}>
							{langPack('npo.members')}
						</TabsTrigger>
						<TabsTrigger value='roles' disabled={!npo_address}>
							{langPack('npo.roles')}
						</TabsTrigger>
						<TabsTrigger value='tokens' disabled={!npo_address}>
							{langPack('npo.availableTitle')}
						</TabsTrigger>
					</TabsList>
					<TabsContent value='main' className='p-2'>
						<MainNpo npo={result} setTab={setCurrentTab} />
					</TabsContent>
					{npo_address && (
						<>
							<TabsContent value='roles' className='p-2'>
								<RolesNpo id={npo_address} />
							</TabsContent>
							<TabsContent value='members' className='p-2'>
								<MembersNpo npo_address={npo_address} />
							</TabsContent>
							<TabsContent value='tokens' className='p-2'>
								<TokensNpo npo_address={npo_address} />
							</TabsContent>
						</>
					)}
				</Tabs>
			) : (
				<MainNpo npo={result} setTab={setCurrentTab} />
			)}
		</div>
	)
}

export default NPOPage
